import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import usePageMeta from '@/hooks/usePageMeta';

const ShopPage = () => {
  usePageMeta({
    title: 'Shop – In Kürze verfügbar | gowiTEC',
    description: 'Der gowiTEC Online-Shop öffnet bald. Systemkomponenten, SPS-Technik und Automatisierungslösungen – demnächst direkt online bestellbar.',
    keywords: 'gowiTEC Shop, Systemkomponenten kaufen, SPS-Technik online',
    canonical: 'https://gowitec.de/shop',
  });

  const features = [
    { icon: 'ri-cpu-line', title: 'Systemkomponenten', desc: 'Hochwertige Komponenten für Ihre Automatisierungsanlage' },
    { icon: 'ri-settings-3-line', title: 'SPS-Technik', desc: 'Steuerungstechnik von führenden Herstellern' },
    { icon: 'ri-flashlight-line', title: 'Schaltschrank-Zubehör', desc: 'Alles für den professionellen Schaltschrankbau' },
    { icon: 'ri-shield-check-line', title: 'Geprüfte Qualität', desc: 'Nur zertifizierte Produkte namhafter Hersteller' },
  ];

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Navbar />

      <main className="flex-1 flex flex-col">
        {/* Hero-Streifen */}
        <section className="bg-[#0f1f4a] pt-32 pb-16">
          <div className="max-w-7xl mx-auto px-6 md:px-10">
            <div className="flex items-center gap-2 mb-4">
              <span className="w-8 h-0.5 bg-[#DC2626]"></span>
              <span className="text-[#DC2626] text-xs font-semibold tracking-widest uppercase">Online-Shop</span>
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white leading-tight">
              Unser Shop
            </h1>
          </div>
        </section>

        {/* Hauptinhalt */}
        <section className="flex-1 py-20 bg-[#F8FAFC]">
          <div className="max-w-3xl mx-auto px-6 md:px-10">

            {/* Haupt-Card */}
            <div className="bg-white rounded-2xl border border-gray-100 p-10 md:p-14 text-center mb-10">

              {/* Icon */}
              <div className="w-24 h-24 flex items-center justify-center mx-auto mb-6 bg-[#EFF6FF] rounded-full">
                <i className="ri-store-2-line text-5xl text-[#1E3A8A]"></i>
              </div>

              {/* Badge */}
              <span className="inline-block bg-amber-100 text-amber-700 text-xs font-semibold px-4 py-1.5 rounded-full mb-5 tracking-widest uppercase">
                In Kürze verfügbar
              </span>

              {/* Titel */}
              <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
                Unser Online-Shop öffnet bald!
              </h2>

              {/* Text */}
              <p className="text-gray-500 text-base leading-relaxed max-w-md mx-auto mb-10">
                Wir arbeiten mit Hochdruck daran, Ihnen ein erstklassiges Einkaufserlebnis zu bieten.
                Bald können Sie unsere Produkte bequem online bestellen – schnell, sicher und direkt vom Hersteller.
              </p>

              {/* Features Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-10 text-left">
                {features.map((f) => (
                  <div key={f.title} className="flex items-start gap-4 bg-[#F8FAFC] rounded-xl p-5">
                    <div className="w-10 h-10 flex items-center justify-center bg-[#EFF6FF] rounded-lg shrink-0">
                      <i className={`${f.icon} text-[#1E3A8A] text-lg`}></i>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-800 mb-0.5">{f.title}</div>
                      <div className="text-xs text-gray-500 leading-relaxed">{f.desc}</div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Divider */}
              <div className="border-t border-gray-100 pt-8 mb-8">
                <p className="text-sm text-gray-500 mb-4">
                  Sie haben bereits eine konkrete Anfrage? Wir helfen Ihnen gerne persönlich weiter.
                </p>
                <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                  <a
                    href="tel:+491776512122"
                    className="flex items-center gap-3 cursor-pointer group"
                    aria-label="Telefon: +49 1776512122"
                  >
                    <div className="w-10 h-10 flex items-center justify-center bg-[#EFF6FF] rounded-xl shrink-0">
                      <i className="ri-phone-line text-[#1E3A8A] text-lg"></i>
                    </div>
                    <div className="text-left">
                      <div className="text-gray-400 text-xs">Rufen Sie uns an</div>
                      <div className="text-gray-900 font-semibold text-sm group-hover:text-[#1E3A8A] transition-colors">+49 1776512122</div>
                    </div>
                  </a>
                  <div className="hidden sm:block w-px h-10 bg-gray-200"></div>
                  <a
                    href="mailto:info@gowitec.de"
                    className="flex items-center gap-3 cursor-pointer group"
                    aria-label="E-Mail: info@gowitec.de"
                  >
                    <div className="w-10 h-10 flex items-center justify-center bg-[#EFF6FF] rounded-xl shrink-0">
                      <i className="ri-mail-line text-[#1E3A8A] text-lg"></i>
                    </div>
                    <div className="text-left">
                      <div className="text-gray-400 text-xs">Per E-Mail</div>
                      <div className="text-gray-900 font-semibold text-sm group-hover:text-[#1E3A8A] transition-colors">info@gowitec.de</div>
                    </div>
                  </a>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/"
                  className="bg-[#1E3A8A] text-white text-sm font-semibold px-7 py-3 rounded-md hover:bg-[#1e40af] transition-colors whitespace-nowrap cursor-pointer w-full sm:w-auto text-center"
                >
                  Zur Startseite
                </Link>
                <Link
                  to="/kontakt"
                  className="border border-gray-200 text-gray-700 text-sm font-medium px-7 py-3 rounded-md hover:border-[#1E3A8A] hover:text-[#1E3A8A] transition-colors whitespace-nowrap cursor-pointer w-full sm:w-auto text-center"
                >
                  Kontakt aufnehmen
                </Link>
              </div>
            </div>

          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default ShopPage;
